import { Shield, Lock, FileText, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { contactInfo } from "../data";

export default function PrivacyPolicy() {
  return (
    <div className="bg-white overflow-hidden">
      {/* Header Section */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 flex items-center justify-center text-center">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/treatment-detail.png"
            alt="Privatlivspolitik baggrund"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"></div>
        </div>
        
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 mx-auto max-w-4xl px-6 lg:px-8"
        >
          <h1 className="text-4xl font-light tracking-tight text-white sm:text-6xl">Samtykke & Privatliv</h1>
          <p className="mt-6 text-lg text-stone-200 max-w-xl mx-auto">
            Information om samtykkeerklæring, journalføring og beskyttelse af dine personlige oplysninger hos Juhler Fodpleje.
          </p>
        </motion.div>
      </section>

      {/* Content Section */}
      <section className="py-16 lg:py-24">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          
          {/* Highlight Box: Samtykkeerklæring */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-orange-50/60 rounded-3xl p-8 border border-orange-100 mb-16"
          >
            <div className="flex flex-col md:flex-row gap-6 items-start">
              <div className="p-4 bg-orange-100 rounded-2xl text-orange-900 shrink-0">
                <FileText className="h-8 w-8" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-stone-900 mb-3">
                  Obligatorisk samtykkeerklæring før første behandling
                </h2>
                <p className="text-stone-700 leading-relaxed mb-4">
                  For at kunne tilbyde dig den mest optimale og sikre fodbehandling, underskriver alle nye kunder en skriftlig samtykkeerklæring forud for den allerførste behandling.
                </p>
                <div className="grid gap-3 sm:grid-cols-2 mt-6">
                  <div className="flex items-center gap-2 text-stone-800 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0" />
                    <span>Underskrives før 1. behandling</span>
                  </div>
                  <div className="flex items-center gap-2 text-stone-800 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0" />
                    <span>Opbevares sikkert & fortroligt</span>
                  </div>
                  <div className="flex items-center gap-2 text-stone-800 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0" />
                    <span>Kan altid tilbagekaldes</span>
                  </div>
                  <div className="flex items-center gap-2 text-stone-800 text-sm">
                    <CheckCircle2 className="h-5 w-5 text-orange-600 shrink-0" />
                    <span>Overholder alle GDPR-regler</span>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Privacy Policy Text */}
          <div className="prose prose-stone max-w-none space-y-12 text-stone-600">
            <div>
              <h2 className="text-3xl font-light text-stone-900 mb-6">Privatlivspolitik & GDPR</h2>
              <p className="text-lg leading-relaxed">
                Hos Juhler Fodpleje vægter jeg datasikkerhed og diskretion utrolig højt. Her kan du læse mere om, hvordan jeg indsamler, behandler og beskytter dine personoplysninger i overensstemmelse med EU's persondataforordning (GDPR).
              </p>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Shield className="h-5 w-5 text-orange-600" />
                1. Hvilke oplysninger indsamles og hvorfor?
              </h3>
              <p className="leading-relaxed mb-4">
                Som lægeexamineret fodplejer har jeg brug for at registrere visse oplysninger om dig for at kunne give dig en faglig korrekt, sikker og individuelt tilpasset behandling. Jeg indsamler følgende:
              </p>
              <ul className="list-disc pl-6 space-y-2 mb-4">
                <li><strong>Almindelige kontaktoplysninger:</strong> Navn, adresse, telefonnummer og evt. e-mailadresse (bruges til tidsbestilling, ruteplanlægning ved hjemmebesøg samt fakturering).</li>
                <li><strong>Helbredsoplysninger:</strong> Relevante sygdomme (f.eks. diabetes, kredsløbssygdomme, gigt eller allergi), medicinforbrug (f.eks. blodfortyndende medicin) samt dine fødders tilstand (bruges til at sikre, at behandlingen udføres sikkert og uden risiko for dit helbred).</li>
                <li><strong>Behandlingshistorik:</strong> Journalnotater over udførte behandlinger, observationer og rådgivning (bruges til at følge udviklingen i din fodsundhed fra gang til gang).</li>
              </ul>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4 flex items-center gap-2">
                <Lock className="h-5 w-5 text-orange-600" />
                2. Sikker opbevaring og fortrolighed
              </h3>
              <p className="leading-relaxed">
                Dine oplysninger og din journal opbevares fuldstændig sikkert, aflåst og fortroligt. Jeg har tavshedspligt, og dine oplysninger bliver aldrig delt med tredjepart (f.eks. din læge eller pårørende) uden dit forudgående, udtrykkelige samtykke. Oplysningerne opbevares kun så længe, det er nødvendigt af hensyn til behandlingen og gældende lovgivning, hvorefter de slettes eller makuleres sikkert.
              </p>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4">
                3. Dine rettigheder
              </h3>
              <p className="leading-relaxed mb-4">
                Du har til enhver tid fuld kontrol over dine egne data. Du har ret til at:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Få indsigt i, hvilke oplysninger jeg har registreret om dig (få udleveret en kopi af din journal).</li>
                <li>Få rettet eventuelle ukorrekte eller forældede oplysninger.</li>
                <li>Få slettet dine oplysninger og din journal, såfremt der ikke er lovmæssige krav om opbevaring.</li>
                <li>Trække dit samtykke til journalføring og databehandling tilbage. Bemærk dog, at jeg uden samtykke til journalføring af helbredsoplysninger ikke kan tilbyde dig fodbehandling af sikkerhedsmæssige årsager.</li>
              </ul>
            </div>

            <div className="border-t border-stone-100 pt-8">
              <h3 className="text-xl font-semibold text-stone-900 mb-4">
                4. Dataansvarlig & Kontakt
              </h3>
              <p className="leading-relaxed">
                Juhler Fodpleje er dataansvarlig for behandlingen af dine personoplysninger. Hvis du har spørgsmål til min privatlivspolitik, ønsker indsigt i dine data, eller ønsker at trække et samtykke tilbage, er du altid velkommen til at kontakte mig:
              </p>
              <div className="mt-6 bg-stone-50 rounded-2xl p-6 border border-stone-100 space-y-2 text-sm text-stone-700">
                <p className="font-semibold text-stone-900">{contactInfo.name}</p>
                <p>v/ {contactInfo.owner}</p>
                <p>{contactInfo.address}, {contactInfo.zipCity}</p>
                <p>Telefon: <a href={`tel:${contactInfo.phone}`} className="text-orange-900 hover:underline">{contactInfo.phoneDisplay}</a></p>
                <p>E-mail: <a href={`mailto:${contactInfo.email}`} className="text-orange-900 hover:underline">{contactInfo.email}</a></p>
              </div>
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}
